import { State, TopicData } from "@/shared/state/visualization";
import { LayoutType, PanelState } from "@/shared/state/visualization/schema";

import {
  getMessagePathNodeFromRecord,
  messagePathDataType,
  MessagePathNode,
  MessagePathPart,
  MessagePathPartType,
  MessagePathRecord,
  PathParser,
  TopicRecord,
} from "../topics";

import { LayoutDefinition } from "./LayoutDefinition";
import {
  isStructuredRequirement,
  Requirement,
  requirementDataType,
  LayoutPanelState,
  Rule,
  RuleStyle,
  StructuredRequirement,
} from "./LayoutRecord";
import { LATEST_LAYOUT_SCHEMA_VERSION } from "./schemas";

export class LayoutDefinitionBuilder {
  static createRequirementFromTopicData(
    topicData: TopicData,
  ): StructuredRequirement {
    return LayoutDefinitionBuilder.createRequirement(
      topicData.topic.name,
      topicData.messagePath.parts,
    );
  }

  static createRequirementFromMessagePathRecord(
    topicRecord: TopicRecord,
    messagePathRecord: MessagePathRecord,
  ): StructuredRequirement {
    const [messagePathNode] = getMessagePathNodeFromRecord(
      topicRecord,
      messagePathRecord,
    );

    return LayoutDefinitionBuilder.createRequirement(
      topicRecord.topic_name,
      messagePathNode.toParts(),
    );
  }

  /**
   * Writes both spellings onto one requirement: `topicName` and `messagePath`,
   * which decide what it matches, and `topicMessagePath` with `dataType`,
   * which restate them for a reader that understands only the joined spelling
   * (see {@link StructuredRequirement}).
   */
  private static createRequirement(
    topicName: string,
    messagePath: MessagePathPart[],
  ): StructuredRequirement {
    const topicMessagePath =
      messagePath.length > 0
        ? `${topicName}${PathParser.TOPIC_SEPARATOR}${MessagePathNode.partsToString(messagePath)}`
        : topicName;

    const requirement: StructuredRequirement = {
      topicName,
      // Copy the parts: callers hand in workspace state or the array a
      // MessagePathNode caches, and this requirement is stored in the layout.
      messagePath: [...messagePath],
      topicMessagePath,
    };

    const dataType = messagePathDataType(messagePath);
    if (dataType !== undefined) {
      requirement.dataType = dataType;
    }

    return requirement;
  }

  private panels: Record<string, LayoutPanelState>;
  private layout: State["layout"];
  private rules: Rule[];
  private ruleRegistry: RuleRegistry;

  constructor(layoutDefinition?: LayoutDefinition) {
    if (layoutDefinition) {
      this.panels = { ...layoutDefinition.state.panels };
      this.layout = { ...layoutDefinition.state.layout };
      this.rules = [...layoutDefinition.state.rules];
      this.ruleRegistry = new RuleRegistry();

      // Register the stored rules under their saved ids, so a panel re-added
      // with requirements the layout already covers reuses that rule rather
      // than a duplicate under a fresh id.
      this.rules.forEach((rule) => {
        this.ruleRegistry.setRuleId(rule.id, rule.requirements, rule.style);
      });

      return;
    }

    this.panels = {};
    this.layout = {
      id: "root",
      axis: "x",
      relativeSize: 1,
      children: [],
    };
    this.rules = [];
    this.ruleRegistry = new RuleRegistry();
  }

  /**
   * Appends a panel beside the ones already in the layout and resizes them all
   * to an equal share. Panels sit side by side on one axis; nested splits are
   * not supported.
   */
  public addPanelToLayout(panelId: string): this {
    this.layout.children.push({
      id: panelId,
      type: LayoutType.Panel,
      relativeSize: 1,
      isResizing: false,
    });

    const numChildren = this.layout.children.length;
    const newSize = 1 / numChildren;

    this.layout.children = this.layout.children.map((child) => ({
      ...child,
      relativeSize: newSize,
    }));

    return this;
  }

  public addRule(requirements: Requirement[], style?: RuleStyle): string {
    const isNewRule = !this.ruleRegistry.hasRule(requirements, style);
    const ruleId = this.ruleRegistry.getRuleId(requirements, style);

    if (isNewRule) {
      this.rules.push({ id: ruleId, requirements, ...(style && { style }) });
    }

    return ruleId;
  }

  public addPanel(panel: Omit<PanelState, "data">, rules: string[]): this {
    const uniqueRules = new Set(rules);

    this.panels[panel.id] = {
      ...panel,
      rules: Array.from(uniqueRules),
    };

    return this;
  }

  public setLayout(layout: State["layout"]): this {
    this.layout = layout;
    return this;
  }

  /**
   * Removes a panel and drops every rule the removal leaves without a panel.
   */
  public removePanel(panelId: string): this {
    if (this.panels[panelId]) {
      delete this.panels[panelId];
    }

    this.cleanupUnusedRules();

    return this;
  }

  /**
   * Replaces a panel's metadata and the ids of the rules that bind data to it,
   * or adds the panel when the layout holds none under `panelId`.
   */
  public updatePanel(
    panelId: string,
    panelMetadata: Omit<PanelState, "data">,
    rules: string[],
  ): this {
    const uniqueRules = new Set(rules);

    if (!this.panels[panelId]) {
      return this.addPanel(panelMetadata, Array.from(uniqueRules));
    }

    this.panels[panelId] = {
      ...panelMetadata,
      id: panelId,
      rules: Array.from(uniqueRules),
    };

    return this;
  }

  public build(): LayoutDefinition {
    this.cleanupUnusedRules();

    const layoutState = {
      panels: this.panels,
      layout: this.layout,
      rules: this.rules,
    };

    // Stamp the schema this code writes to rather than the one the seeding
    // definition carried. Rules inherited from that definition pass through
    // untouched, so the latest schema has to keep accepting their shape.
    return new LayoutDefinition(layoutState, LATEST_LAYOUT_SCHEMA_VERSION);
  }

  /**
   * Removes rules that are no longer used by any panel
   */
  private cleanupUnusedRules(): void {
    const usedRuleIds = new Set<string>();
    Object.values(this.panels).forEach((panel) => {
      panel.rules.forEach((ruleId) => usedRuleIds.add(ruleId));
    });

    this.rules = this.rules.filter((rule) => usedRuleIds.has(rule.id));
  }
}

class RuleRegistry {
  /**
   * Renders a single requirement as a string that two requirements share
   * exactly when they ask for the same data.
   *
   * A requirement carrying split fields signs as its topic name, its parts,
   * and the data type of its last attribute part. Matching compares part kinds
   * and attribute names, and it copies slice bounds onto whichever node
   * matched, so bounds change the data a rule receives and are signed as well;
   * the per-part data types are dropped, since only the last one is matched
   * on. The joined spelling written onto the same requirement repeats all of
   * this, so it is left out. An open-ended slice end is Infinity in memory
   * and null once the parts have been through JSON, and JSON.stringify writes
   * both as null, so the two spellings sign identically.
   *
   * A requirement holding only the joined spelling signs as that string under
   * a different tag: two topic-name/message-path splits can join to the same
   * string, so signing them alike would merge rules that ask for different
   * data.
   */
  private static createRequirementSignature(requirement: Requirement): string {
    const dataType = requirementDataType(requirement) ?? null;

    if (!isStructuredRequirement(requirement)) {
      return JSON.stringify(["joined", requirement.topicMessagePath, dataType]);
    }

    const messagePath = requirement.messagePath.map((part) =>
      part.type === MessagePathPartType.Attr
        ? [part.type, part.attribute]
        : [part.type, part.start, part.end ?? null],
    );

    return JSON.stringify([
      "structured",
      requirement.topicName,
      messagePath,
      dataType,
    ]);
  }

  /**
   * Renders a whole rule as a string that two rules share exactly when they
   * ask for the same data with the same styling. The requirement signatures
   * are sorted, so requirement order is no part of rule identity: the same
   * requirements in another order sign identically, take the id already
   * minted, and leave the stored rule holding the order it was first
   * registered with. That stored order is the one a match binds topics in,
   * and the one a style addressing matched topics by position (a 3D
   * entity's position axes) indexes into.
   */
  private static createRuleSignature(
    requirements: Requirement[],
    style?: RuleStyle,
  ): string {
    const requirementSignatures = requirements
      .map(RuleRegistry.createRequirementSignature)
      .sort();

    return JSON.stringify([requirementSignatures, style ?? null]);
  }

  private signatureToRuleId = new Map<string, string>();

  /**
   * The id these requirements and style are registered under, minted and
   * remembered on first sight. Minting is silent, so a caller that needs to
   * know whether the id is new asks {@link hasRule} first.
   */
  public getRuleId(requirements: Requirement[], style?: RuleStyle): string {
    const signature = RuleRegistry.createRuleSignature(requirements, style);

    if (!this.signatureToRuleId.has(signature)) {
      this.signatureToRuleId.set(
        signature,
        `rule$${crypto.randomUUID().substring(0, 8)}`,
      );
    }

    return this.signatureToRuleId.get(signature)!;
  }

  public hasRule(requirements: Requirement[], style?: RuleStyle): boolean {
    return this.signatureToRuleId.has(
      RuleRegistry.createRuleSignature(requirements, style),
    );
  }

  public setRuleId(
    ruleId: string,
    requirements: Requirement[],
    style?: RuleStyle,
  ): void {
    this.signatureToRuleId.set(
      RuleRegistry.createRuleSignature(requirements, style),
      ruleId,
    );
  }
}
